import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetPrinters,
  GetPrintConfig,
  CreatePrinter,
  UpdatePrinter,
  DeletePrinter } from "../Api";
import { 
  ServiceQueryOptions,
  unwrapApiResponse } from "./query_helpers";
import { LOOKUP_STALE_TIME } from "../Constant/AppConstants";

export const printerQueryKeys = {
  printers: (tenantId?: string) => ["printers", tenantId] as const,
};

export const invalidatePrinterQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["printers"] });
};

export const usePrintersQuery = (
  tenantId?: string,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: printerQueryKeys.printers(tenantId),
    queryFn: async () =>
      unwrapApiResponse(await GetPrinters({ tenant_id: tenantId })),
  });
};

export const usePrintConfigQuery = (
  storeId?: string,
  options?: ServiceQueryOptions,
) => {
  const enabled = storeId ? Boolean(storeId) : true;
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: ["printConfig", storeId],
    queryFn: async () =>
      unwrapApiResponse(await GetPrintConfig({ store_id: storeId })),
    enabled: enabled && (options?.enabled !== false),
  });
};

export const usePrintersRequest = (tenantId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: printerQueryKeys.printers(tenantId),
        queryFn: async () =>
          unwrapApiResponse(await GetPrinters({ tenant_id: tenantId })),
      }),
  });
};

export const useCreatePrinterMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreatePrinter>[0]) =>
      CreatePrinter(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidatePrinterQueries(queryClient);
      }
    },
  });
};

export const useUpdatePrinterMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdatePrinter>[0]) =>
      UpdatePrinter(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidatePrinterQueries(queryClient);
      }
    },
  });
};

export const useDeletePrinterMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeletePrinter>[0]) =>
      DeletePrinter(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidatePrinterQueries(queryClient);
      }
    },
  });
};

export const usePrinterService = (config?: {
  tenantId?: string;
  fetchPrintersEnabled?: boolean;
}) => {
  const printersQuery = usePrintersQuery(config?.tenantId, {
    enabled: config?.fetchPrintersEnabled !== false,
  });

  const createPrinterMutation = useCreatePrinterMutation();
  const updatePrinterMutation = useUpdatePrinterMutation();
  const deletePrinterMutation = useDeletePrinterMutation();

  return {
    printersQuery,
    createPrinterMutation,
    updatePrinterMutation,
    deletePrinterMutation,
  };
};
